import { computed, ref } from 'vue'
import type { ComputedRef, Ref } from 'vue'
import {
  abortGalleryUpload,
  completeGalleryUpload,
  fetchGalleryStorage,
  listGalleryItems,
  reserveGalleryUpload,
  retryGalleryItem,
  signGalleryUploadParts,
} from '@/api/galleries'
import { GalleryItemStatus, UploadTransport } from '@/types/galleries'
import type {
  GalleryStorageLimits,
  OrganizerGalleryItem,
  ReserveUploadPayload,
  SignedPart,
  UploadTicket,
} from '@/types/galleries'
import { parseGalleryApiError } from '@/utils/galleries/apiError'
import { isLayoutConsistent, partRange } from '@/utils/galleries/partSizing'
import { pollWithBackoff } from '@/utils/galleries/pollBackoff'
import {
  UploadFailureReason,
  failureForItemErrorCode,
  outcomeForApiError,
} from '@/utils/galleries/uploadFailure'
import type { UploadFailure } from '@/utils/galleries/uploadFailure'
import { UploadRejectReason, classifyFile, planUploads } from '@/utils/galleries/uploadPlan'
import type { UploadKind } from '@/utils/galleries/uploadPlan'
import { UnsupportedImageError, downscaleImage } from '@/utils/media/downscaleImage'
import { captureVideoPoster } from '@/utils/media/captureVideoPoster'
import { stripMp4Metadata } from '@/utils/media/stripMp4Metadata'
import {
  UploadAbortedError,
  UploadHttpError,
  uploadToPresignedUrl,
} from '@/utils/media/uploadToPresignedUrl'

// ── Public types ─────────────────────────────────────────────────────────────

export const UploadStatus = {
  Queued: 'queued',
  Preparing: 'preparing',
  Prepared: 'prepared',
  AwaitingDecision: 'awaiting_decision',
  Reserving: 'reserving',
  Uploading: 'uploading',
  Completing: 'completing',
  Processing: 'processing',
  Ready: 'ready',
  Failed: 'failed',
  Cancelled: 'cancelled',
} as const
export type UploadStatus = typeof UploadStatus[keyof typeof UploadStatus]

export interface UploadEntry {
  id: string
  batchId: string
  eventSlug: string
  galleryId: string
  fileName: string
  kind: UploadKind
  status: UploadStatus
  /** Prepared size in bytes (original size until preparation has finished). */
  bytes: number
  /** 0 to 100, progress of the main file. */
  progress: number
  failure: UploadFailure | null
  uploadId: string | null
  itemId: string | null
  /** Object URL of the local file (images) or captured poster (videos). */
  previewUrl: string | null
  /** Earliest time (ms epoch) at which the entry may be reserved again. */
  notBefore: number
}

export interface BatchRejection {
  name: string
  reason: UploadRejectReason
}

export interface BatchNoticeFile {
  id: string
  name: string
  bytes: number
}

export interface BatchNotice {
  batchId: string
  eventSlug: string
  galleryId: string
  /** Files that fit in the remaining storage. */
  fitCount: number
  /** Files that do not fit. When not empty the uploads wait for a decision. */
  doesNotFit: BatchNoticeFile[]
  rejected: BatchRejection[]
  remainingBytes: number
  needsDecision: boolean
}

export interface EnqueueOptions {
  keepOriginal: boolean
  limits: GalleryStorageLimits
  /** Fallback when the fresh storage summary cannot be fetched. */
  remainingBytes: number
}

type ItemListener = (entry: UploadEntry, item: OrganizerGalleryItem) => void

// ── Tunables ─────────────────────────────────────────────────────────────────

export const MAX_FILES_IN_FLIGHT = 3
export const MAX_PREPARING = 2
export const MAX_PARTS_PER_FILE = 3
export const MAX_CONCURRENT_PUTS = 4
export const PART_SIGN_BATCH = 20
export const PART_RETRY_DELAYS_MS = [1000, 4000, 10000] as const
export const REQUEUE_DELAY_MS = 5000

// ── Module-level state (no Pinia, same pattern as useSupportUnreadCount) ─────

interface Internal {
  file: File
  blob: Blob | null
  mime: string
  width: number
  height: number
  durationSeconds: number | null
  poster: { blob: Blob; mime: string } | null
  fileName: string
  keepOriginal: boolean
  abort: AbortController | null
}

interface BatchState {
  eventSlug: string
  galleryId: string
  options: EnqueueOptions
  rejected: BatchRejection[]
  planned: boolean
  released: boolean
}

const entries = ref<UploadEntry[]>([])
const notices = ref<BatchNotice[]>([])
const internals = new Map<string, Internal>()
const batches = new Map<string, BatchState>()
const createdListeners = new Set<ItemListener>()
const updatedListeners = new Set<ItemListener>()
let idSeq = 0
let stopPolling: (() => void) | null = null
let beforeUnloadAttached = false
let activePuts = 0
const putWaiters: Array<() => void> = []

// ── Small helpers ────────────────────────────────────────────────────────────

function getEntry(id: string): UploadEntry | undefined {
  return entries.value.find((e) => e.id === id)
}

function isCancelled(id: string): boolean {
  const entry = getEntry(id)
  return entry === undefined || entry.status === UploadStatus.Cancelled
}

function setStatus(entry: UploadEntry, status: UploadStatus): void {
  entry.status = status
  syncBeforeUnload()
}

function fail(entry: UploadEntry, failure: UploadFailure): void {
  entry.failure = failure
  setStatus(entry, UploadStatus.Failed)
  // A failed upload never holds quota: release the reservation now instead of waiting for it to expire.
  // A retry makes a fresh reservation for the same prepared blob.
  if (entry.uploadId !== null && entry.itemId === null) {
    void bestEffortAbort(entry.eventSlug, entry.galleryId, entry.uploadId)
    entry.uploadId = null
  }
}

function revokePreview(entry: UploadEntry): void {
  if (entry.previewUrl !== null) {
    URL.revokeObjectURL(entry.previewUrl)
    entry.previewUrl = null
  }
}

function releaseBlobs(id: string): void {
  const internal = internals.get(id)
  if (internal === undefined) return
  internal.blob = null
  internal.poster = null
}

function delay(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    if (signal?.aborted) {
      reject(new UploadAbortedError())
      return
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort)
      resolve()
    }, ms)
    const onAbort = (): void => {
      clearTimeout(timer)
      reject(new UploadAbortedError())
    }
    signal?.addEventListener('abort', onAbort, { once: true })
  })
}

async function withPutSlot<T>(task: () => Promise<T>): Promise<T> {
  if (activePuts >= MAX_CONCURRENT_PUTS) {
    await new Promise<void>((resolve) => putWaiters.push(resolve))
  }
  activePuts += 1
  try {
    return await task()
  } finally {
    activePuts -= 1
    putWaiters.shift()?.()
  }
}

// ── beforeunload ─────────────────────────────────────────────────────────────

const UNLOAD_STATUSES: UploadStatus[] = [
  UploadStatus.Queued,
  UploadStatus.Preparing,
  UploadStatus.Prepared,
  UploadStatus.AwaitingDecision,
  UploadStatus.Reserving,
  UploadStatus.Uploading,
  UploadStatus.Completing,
]

function handleBeforeUnload(event: BeforeUnloadEvent): void {
  event.preventDefault()
  // Legacy browsers need a returnValue to show the prompt.
  event.returnValue = ''
}

function syncBeforeUnload(): void {
  const needed = entries.value.some((e) => UNLOAD_STATUSES.includes(e.status))
  if (needed && !beforeUnloadAttached) {
    window.addEventListener('beforeunload', handleBeforeUnload)
    beforeUnloadAttached = true
  } else if (!needed && beforeUnloadAttached) {
    window.removeEventListener('beforeunload', handleBeforeUnload)
    beforeUnloadAttached = false
  }
}

// ── Enqueue and preparation ──────────────────────────────────────────────────

function enqueue(eventSlug: string, galleryId: string, files: File[], options: EnqueueOptions): string {
  idSeq += 1
  const batchId = `batch-${idSeq}`
  const rejected: BatchRejection[] = []
  const batch: BatchState = { eventSlug, galleryId, options, rejected, planned: false, released: false }
  batches.set(batchId, batch)

  files.forEach((file, index) => {
    if (index >= options.limits.max_files_per_batch) {
      rejected.push({ name: file.name, reason: UploadRejectReason.BatchLimit })
      return
    }
    const classification = classifyFile(file)
    if (!classification.ok) {
      rejected.push({ name: file.name, reason: classification.reason })
      return
    }
    idSeq += 1
    const id = `upload-${idSeq}`
    const entry: UploadEntry = {
      id,
      batchId,
      eventSlug,
      galleryId,
      fileName: file.name,
      kind: classification.kind,
      status: UploadStatus.Queued,
      bytes: file.size,
      progress: 0,
      failure: null,
      uploadId: null,
      itemId: null,
      previewUrl: classification.kind === 'image' && !classification.needsConversion ? URL.createObjectURL(file) : null,
      notBefore: 0,
    }
    internals.set(id, {
      file,
      blob: null,
      mime: classification.mime,
      width: 0,
      height: 0,
      durationSeconds: null,
      poster: null,
      fileName: file.name,
      keepOriginal: options.keepOriginal,
      abort: null,
    })
    entries.value.push(entry)
  })

  syncBeforeUnload()
  pumpPrepare()
  checkBatch(batchId)
  return batchId
}

function pumpPrepare(): void {
  let preparing = entries.value.filter((e) => e.status === UploadStatus.Preparing).length
  for (const entry of entries.value) {
    if (preparing >= MAX_PREPARING) break
    if (entry.status !== UploadStatus.Queued) continue
    preparing += 1
    void prepare(entry.id)
  }
}

async function prepare(id: string): Promise<void> {
  const entry = getEntry(id)
  const internal = internals.get(id)
  if (entry === undefined || internal === undefined) return
  setStatus(entry, UploadStatus.Preparing)
  entry.failure = null

  try {
    if (entry.kind === 'image') {
      const prepared = await downscaleImage(internal.file, { keepOriginal: internal.keepOriginal })
      internal.blob = prepared.blob
      internal.mime = prepared.mime
      internal.width = prepared.width
      internal.height = prepared.height
      internal.fileName = prepared.name
      entry.bytes = prepared.bytes
    } else {
      const capture = await captureVideoPoster(internal.file)
      if (capture !== null) {
        internal.poster = { blob: capture.poster, mime: capture.posterMime }
        internal.width = capture.width
        internal.height = capture.height
        internal.durationSeconds = capture.durationSeconds
        entry.previewUrl = URL.createObjectURL(capture.poster)
      }
      internal.blob = internal.mime === 'video/mp4' ? await stripMp4Metadata(internal.file) : internal.file
      entry.bytes = internal.blob.size
    }
    if (isCancelled(id)) return
    setStatus(entry, UploadStatus.Prepared)
  } catch (error) {
    if (isCancelled(id)) return
    const heic = error instanceof UnsupportedImageError && error.heic
    fail(entry, {
      reason: heic ? UploadRejectReason.HeicUnsupported : UploadFailureReason.PrepareFailed,
      retryable: !heic,
    })
  } finally {
    pumpPrepare()
    checkBatch(entry.batchId)
    pumpUpload()
  }
}

function checkBatch(batchId: string): void {
  const batch = batches.get(batchId)
  if (batch === undefined || batch.planned) return
  const pending = entries.value.some(
    (e) => e.batchId === batchId && (e.status === UploadStatus.Queued || e.status === UploadStatus.Preparing),
  )
  if (pending) return
  batch.planned = true
  void evaluateBatch(batchId)
}

async function evaluateBatch(batchId: string): Promise<void> {
  const batch = batches.get(batchId)
  if (batch === undefined) return
  const prepared = entries.value.filter((e) => e.batchId === batchId && e.status === UploadStatus.Prepared)

  let remaining = batch.options.remainingBytes
  if (prepared.length > 0) {
    try {
      const summary = await fetchGalleryStorage(batch.eventSlug)
      remaining = summary.uploads_blocked ? 0 : summary.remaining_bytes
    } catch {
      // Fall back to the snapshot taken when the files were dropped.
    }
  }

  const plan = planUploads(
    prepared.map((e) => ({ id: e.id, bytes: e.bytes, kind: e.kind })),
    remaining,
    batch.options.limits.image_max_bytes,
  )

  for (const rejection of plan.rejected) {
    const entry = getEntry(rejection.id)
    if (entry === undefined) continue
    batch.rejected.push({ name: entry.fileName, reason: rejection.reason })
    fail(entry, { reason: UploadFailureReason.ImageTooLarge, retryable: false })
  }

  const needsDecision = plan.doesNotFit.length > 0
  if (needsDecision) {
    for (const id of [...plan.fits, ...plan.doesNotFit]) {
      const entry = getEntry(id)
      if (entry !== undefined) setStatus(entry, UploadStatus.AwaitingDecision)
    }
  }

  if (needsDecision || batch.rejected.length > 0) {
    notices.value = [
      ...notices.value.filter((n) => n.batchId !== batchId),
      {
        batchId,
        eventSlug: batch.eventSlug,
        galleryId: batch.galleryId,
        fitCount: plan.fits.length,
        doesNotFit: plan.doesNotFit.flatMap((id) => {
          const entry = getEntry(id)
          return entry === undefined ? [] : [{ id, name: entry.fileName, bytes: entry.bytes }]
        }),
        rejected: [...batch.rejected],
        remainingBytes: remaining,
        needsDecision,
      },
    ]
  }

  batch.released = true
  pumpUpload()
}

/** Uploads the files that fit and marks the others as failed (not enough storage). */
function confirmPlan(batchId: string): void {
  for (const entry of entries.value.filter((e) => e.batchId === batchId && e.status === UploadStatus.AwaitingDecision)) {
    const notice = notices.value.find((n) => n.batchId === batchId)
    if (notice?.doesNotFit.some((f) => f.id === entry.id)) {
      fail(entry, { reason: UploadFailureReason.NotEnoughStorage, retryable: true })
    } else {
      setStatus(entry, UploadStatus.Prepared)
    }
  }
  dismissNotice(batchId)
  pumpUpload()
}

/** Drops every file of a batch that is still waiting for the storage decision. */
function cancelPlan(batchId: string): void {
  for (const entry of [...entries.value.filter((e) => e.batchId === batchId && e.status === UploadStatus.AwaitingDecision)]) {
    remove(entry.id)
  }
  dismissNotice(batchId)
}

function dismissNotice(batchId: string): void {
  notices.value = notices.value.filter((n) => n.batchId !== batchId)
}

// ── Upload pipeline ──────────────────────────────────────────────────────────

const IN_FLIGHT: UploadStatus[] = [UploadStatus.Reserving, UploadStatus.Uploading, UploadStatus.Completing]

function pumpUpload(): void {
  let inFlight = entries.value.filter((e) => IN_FLIGHT.includes(e.status)).length
  const now = Date.now()
  for (const entry of entries.value) {
    if (inFlight >= MAX_FILES_IN_FLIGHT) break
    if (entry.status !== UploadStatus.Prepared) continue
    if (batches.get(entry.batchId)?.released !== true) continue
    if (entry.notBefore > now) continue
    inFlight += 1
    void runUpload(entry.id)
  }
}

function buildReservePayload(entry: UploadEntry, internal: Internal): ReserveUploadPayload {
  const payload: ReserveUploadPayload = {
    mime: internal.mime,
    bytes: entry.bytes,
    filename: internal.fileName,
  }
  if (internal.width > 0 && internal.height > 0) {
    payload.width = internal.width
    payload.height = internal.height
  }
  if (internal.durationSeconds !== null) payload.duration_seconds = internal.durationSeconds
  if (entry.kind === 'video' && internal.poster !== null) {
    payload.poster = { mime: internal.poster.mime, bytes: internal.poster.blob.size }
  }
  return payload
}

async function runUpload(id: string): Promise<void> {
  const entry = getEntry(id)
  const internal = internals.get(id)
  if (entry === undefined || internal === undefined || internal.blob === null) return

  const controller = new AbortController()
  internal.abort = controller
  entry.progress = 0
  entry.failure = null
  setStatus(entry, UploadStatus.Reserving)

  try {
    const ticket = await reserveGalleryUpload(entry.eventSlug, entry.galleryId, buildReservePayload(entry, internal))
    entry.uploadId = ticket.id
    if (isCancelled(id)) {
      void bestEffortAbort(entry.eventSlug, entry.galleryId, ticket.id)
      return
    }

    setStatus(entry, UploadStatus.Uploading)
    const blob = internal.blob
    if (blob === null) return

    if (ticket.transport === UploadTransport.Multipart) {
      await uploadMultipart(entry, blob, ticket, controller.signal)
    } else {
      await uploadSingle(entry, blob, ticket, controller.signal)
    }

    let posterUploaded = false
    if (internal.poster !== null && ticket.poster_upload !== null) {
      try {
        const posterBlob = internal.poster.blob
        const target = ticket.poster_upload
        await withPutSlot(() => uploadToPresignedUrl(posterBlob, target, () => undefined, controller.signal))
        posterUploaded = true
      } catch (error) {
        if (error instanceof UploadAbortedError) throw error
        // A failed poster never blocks the video: it is created without one.
      }
    }

    if (isCancelled(id)) return
    setStatus(entry, UploadStatus.Completing)
    const item = await completeGalleryUpload(entry.eventSlug, entry.galleryId, ticket.id, posterUploaded)
    if (isCancelled(id)) return

    entry.progress = 100
    entry.itemId = item.id
    releaseBlobs(id)
    applyItemState(entry, item)
    for (const listener of createdListeners) listener(entry, item)
  } catch (error) {
    handleUploadError(entry, error)
  } finally {
    internal.abort = null
    pumpUpload()
  }
}

async function uploadSingle(
  entry: UploadEntry,
  blob: Blob,
  ticket: UploadTicket,
  signal: AbortSignal,
): Promise<void> {
  const target = ticket.upload
  if (target === null) throw new UploadHttpError(0)
  await withPutSlot(() =>
    uploadToPresignedUrl(
      blob,
      target,
      (loaded, total) => {
        entry.progress = total > 0 ? Math.min(99, Math.round((loaded / total) * 100)) : 0
      },
      signal,
    ),
  )
}

async function uploadMultipart(
  entry: UploadEntry,
  blob: Blob,
  ticket: UploadTicket,
  signal: AbortSignal,
): Promise<void> {
  const layout = ticket.multipart
  if (layout === null) throw new UploadHttpError(0)
  const partSize = layout.part_size_bytes
  const partCount = layout.part_count
  if (!isLayoutConsistent({ partSizeBytes: partSize, partCount }, blob.size)) throw new UploadHttpError(0)

  const signed = new Map<number, SignedPart>()
  const loaded = new Array<number>(partCount).fill(0)
  const done = new Set<number>()
  let signing: Promise<void> | null = null
  let nextPart = 1

  const reportProgress = (): void => {
    const sum = loaded.reduce((a, b) => a + b, 0)
    entry.progress = blob.size > 0 ? Math.min(99, Math.round((sum / blob.size) * 100)) : 0
  }

  const signBatch = async (from: number): Promise<void> => {
    const numbers: number[] = []
    for (let n = from; n <= partCount && numbers.length < PART_SIGN_BATCH; n += 1) {
      if (!signed.has(n) && !done.has(n)) numbers.push(n)
    }
    const result = await signGalleryUploadParts(entry.eventSlug, entry.galleryId, ticket.id, numbers)
    for (const part of result.parts) signed.set(part.part_number, part)
  }

  const signedPart = async (n: number): Promise<SignedPart> => {
    while (!signed.has(n)) {
      if (signing === null) {
        signing = signBatch(n).finally(() => {
          signing = null
        })
      }
      await signing
    }
    return signed.get(n) as SignedPart
  }

  const uploadPart = async (n: number): Promise<void> => {
    const { start, end } = partRange(n, partSize, blob.size)
    // Blob.slice without a content type, so the browser adds no Content-Type header.
    const slice = blob.slice(start, end)
    for (let attempt = 0; ; attempt += 1) {
      try {
        let part: SignedPart
        if (attempt === 0) {
          part = await signedPart(n)
        } else {
          // A retry always asks for a fresh URL: the previous one may have expired.
          const fresh = await signGalleryUploadParts(entry.eventSlug, entry.galleryId, ticket.id, [n])
          const found = fresh.parts.find((p) => p.part_number === n)
          if (found === undefined) throw new UploadHttpError(0)
          part = found
        }
        await withPutSlot(() =>
          uploadToPresignedUrl(
            slice,
            part,
            (l) => {
              loaded[n - 1] = l
              reportProgress()
            },
            signal,
          ),
        )
        loaded[n - 1] = end - start
        done.add(n)
        signed.delete(n)
        reportProgress()
        return
      } catch (error) {
        const retryDelay = PART_RETRY_DELAYS_MS[attempt]
        if (!(error instanceof UploadHttpError) || retryDelay === undefined) throw error
        loaded[n - 1] = 0
        reportProgress()
        await delay(retryDelay, signal)
      }
    }
  }

  const worker = async (): Promise<void> => {
    while (nextPart <= partCount) {
      if (signal.aborted) throw new UploadAbortedError()
      const n = nextPart
      nextPart += 1
      await uploadPart(n)
    }
  }

  await Promise.all(Array.from({ length: Math.min(MAX_PARTS_PER_FILE, partCount) }, () => worker()))
}

async function bestEffortAbort(eventSlug: string, galleryId: string, uploadId: string): Promise<void> {
  try {
    await abortGalleryUpload(eventSlug, galleryId, uploadId)
  } catch {
    // The server cleanup command expires the reservation.
  }
}

function handleUploadError(entry: UploadEntry, error: unknown): void {
  if (entry.status === UploadStatus.Cancelled) return
  if (error instanceof UploadAbortedError) return

  if (error instanceof UploadHttpError) {
    const reason = error.status === 413 ? UploadFailureReason.FileTooLarge : UploadFailureReason.UploadFailed
    fail(entry, { reason, retryable: reason !== UploadFailureReason.FileTooLarge })
    return
  }

  const outcome = outcomeForApiError(parseGalleryApiError(error))
  if (outcome.kind === 'requeue') {
    // Too many open reservations in the organisation: try again shortly.
    entry.uploadId = null
    entry.notBefore = Date.now() + REQUEUE_DELAY_MS
    setStatus(entry, UploadStatus.Prepared)
    setTimeout(pumpUpload, REQUEUE_DELAY_MS + 50)
    return
  }
  fail(entry, outcome.failure)
}

// ── After the item exists: processing and polling ────────────────────────────

function applyItemState(entry: UploadEntry, item: OrganizerGalleryItem): void {
  if (item.status === GalleryItemStatus.Ready) {
    entry.failure = null
    setStatus(entry, UploadStatus.Ready)
  } else if (item.status === GalleryItemStatus.Failed) {
    fail(entry, failureForItemErrorCode(item.error_code))
  } else {
    entry.failure = null
    setStatus(entry, UploadStatus.Processing)
    ensurePolling()
  }
}

function ensurePolling(): void {
  if (stopPolling !== null) return
  stopPolling = pollWithBackoff(async () => {
    const processing = entries.value.filter((e) => e.status === UploadStatus.Processing && e.itemId !== null)
    if (processing.length === 0) return true
    const groups = new Map<string, UploadEntry[]>()
    for (const entry of processing) {
      const key = `${entry.eventSlug}|${entry.galleryId}`
      groups.set(key, [...(groups.get(key) ?? []), entry])
    }
    for (const group of groups.values()) {
      const first = group[0]
      if (first === undefined) continue
      const ids = group.flatMap((e) => (e.itemId === null ? [] : [e.itemId]))
      for (let i = 0; i < ids.length; i += 100) {
        try {
          const items = await listGalleryItems(first.eventSlug, first.galleryId, ids.slice(i, i + 100))
          for (const item of items) {
            const entry = group.find((e) => e.itemId === item.id)
            if (entry === undefined) continue
            applyItemState(entry, item)
            for (const listener of updatedListeners) listener(entry, item)
          }
          // Items missing from the response were deleted in the meantime.
          for (const entry of group) {
            if (entry.itemId !== null && ids.slice(i, i + 100).includes(entry.itemId) && !items.some((it) => it.id === entry.itemId)) {
              remove(entry.id)
            }
          }
        } catch {
          // Silent: the next poll retries.
        }
      }
    }
    return !entries.value.some((e) => e.status === UploadStatus.Processing)
  }, {
    onFinish: () => {
      stopPolling = null
    },
  })
}

// ── Cancel, retry, remove ────────────────────────────────────────────────────

function cancel(id: string): void {
  const entry = getEntry(id)
  if (entry === undefined) return
  if (entry.status === UploadStatus.Ready || entry.status === UploadStatus.Cancelled) return
  const wasActive = entry.status !== UploadStatus.Failed
  setStatus(entry, UploadStatus.Cancelled)
  internals.get(id)?.abort?.abort()
  releaseBlobs(id)
  if (wasActive && entry.uploadId !== null && entry.itemId === null) {
    void bestEffortAbort(entry.eventSlug, entry.galleryId, entry.uploadId)
  }
  pumpPrepare()
  pumpUpload()
}

function retry(id: string): void {
  const entry = getEntry(id)
  const internal = internals.get(id)
  if (entry === undefined || entry.status !== UploadStatus.Failed || entry.failure?.retryable !== true) return

  if (entry.itemId !== null) {
    const itemId = entry.itemId
    entry.failure = null
    setStatus(entry, UploadStatus.Processing)
    void (async (): Promise<void> => {
      try {
        const item = await retryGalleryItem(entry.eventSlug, entry.galleryId, itemId)
        applyItemState(entry, item)
        for (const listener of updatedListeners) listener(entry, item)
      } catch {
        fail(entry, { reason: UploadFailureReason.ProcessingFailed, retryable: true })
      }
    })()
    return
  }

  if (entry.uploadId !== null) {
    // The old reservation is released; a new one is made for the same prepared blob.
    void bestEffortAbort(entry.eventSlug, entry.galleryId, entry.uploadId)
    entry.uploadId = null
  }
  entry.progress = 0
  entry.failure = null
  entry.notBefore = 0
  if (internal?.blob !== null && internal?.blob !== undefined) {
    setStatus(entry, UploadStatus.Prepared)
    const batch = batches.get(entry.batchId)
    if (batch !== undefined) batch.released = true
    pumpUpload()
  } else {
    setStatus(entry, UploadStatus.Queued)
    pumpPrepare()
  }
}

function remove(id: string): void {
  const entry = getEntry(id)
  if (entry === undefined) return
  if (entry.status !== UploadStatus.Ready && entry.status !== UploadStatus.Failed && entry.status !== UploadStatus.Cancelled) {
    cancel(id)
  }
  revokePreview(entry)
  internals.delete(id)
  entries.value = entries.value.filter((e) => e.id !== id)
  if (!entries.value.some((e) => e.batchId === entry.batchId)) {
    batches.delete(entry.batchId)
  }
  syncBeforeUnload()
}

function clearFinished(): void {
  const terminal: UploadStatus[] = [UploadStatus.Ready, UploadStatus.Failed, UploadStatus.Cancelled]
  for (const entry of [...entries.value]) {
    if (terminal.includes(entry.status)) remove(entry.id)
  }
}

function onItemCreated(listener: ItemListener): () => void {
  createdListeners.add(listener)
  return (): void => {
    createdListeners.delete(listener)
  }
}

function onItemUpdated(listener: ItemListener): () => void {
  updatedListeners.add(listener)
  return (): void => {
    updatedListeners.delete(listener)
  }
}

/** Local preview URL of an item that was uploaded in this session (while it is processing). */
function previewFor(itemId: string): string | null {
  return entries.value.find((e) => e.itemId === itemId)?.previewUrl ?? null
}

/** Test helper: drops all module state. */
export function resetGalleryUploadManager(): void {
  stopPolling?.()
  stopPolling = null
  for (const entry of entries.value) revokePreview(entry)
  entries.value = []
  notices.value = []
  internals.clear()
  batches.clear()
  createdListeners.clear()
  updatedListeners.clear()
  activePuts = 0
  putWaiters.length = 0
  if (beforeUnloadAttached) {
    window.removeEventListener('beforeunload', handleBeforeUnload)
    beforeUnloadAttached = false
  }
}

export interface GalleryUploadManager {
  entries: Ref<UploadEntry[]>
  notices: Ref<BatchNotice[]>
  /** Entries that have not reached a terminal state. */
  activeCount: ComputedRef<number>
  failedCount: ComputedRef<number>
  doneCount: ComputedRef<number>
  totalCount: ComputedRef<number>
  enqueue: typeof enqueue
  confirmPlan: typeof confirmPlan
  cancelPlan: typeof cancelPlan
  dismissNotice: typeof dismissNotice
  cancel: typeof cancel
  retry: typeof retry
  remove: typeof remove
  clearFinished: typeof clearFinished
  onItemCreated: typeof onItemCreated
  onItemUpdated: typeof onItemUpdated
  previewFor: typeof previewFor
}

const TERMINAL: UploadStatus[] = [UploadStatus.Ready, UploadStatus.Failed, UploadStatus.Cancelled]

const activeCount = computed(() => entries.value.filter((e) => !TERMINAL.includes(e.status)).length)
const failedCount = computed(() => entries.value.filter((e) => e.status === UploadStatus.Failed).length)
const doneCount = computed(() => entries.value.filter((e) => e.status === UploadStatus.Ready).length)
const totalCount = computed(() => entries.value.filter((e) => e.status !== UploadStatus.Cancelled).length)

export function useGalleryUploadManager(): GalleryUploadManager {
  return {
    entries,
    notices,
    activeCount,
    failedCount,
    doneCount,
    totalCount,
    enqueue,
    confirmPlan,
    cancelPlan,
    dismissNotice,
    cancel,
    retry,
    remove,
    clearFinished,
    onItemCreated,
    onItemUpdated,
    previewFor,
  }
}
